import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, 
  DollarSign, 
  Activity, 
  TrendingUp, 
  BarChart3, 
  Crown,
  ArrowLeft,
  RefreshCcw,
  ChevronLeft,
  ChevronRight,
  Edit,
  LineChart as LineChartIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { logger } from '@/lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Edit User Modal State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    email_verified: false,
    is_admin: false,
    plan: 'free',
    analyses_limit: 5,
    analyses_used: 0
  });
  
  // Analytics/Charts Data
  const [analyticsData, setAnalyticsData] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [visitsData, setVisitsData] = useState(null);
  // Demo analytics — server-side event log of every successful demo analysis
  const [demoAnalytics, setDemoAnalytics] = useState(null);
  const [demoAnalyticsWindow, setDemoAnalyticsWindow] = useState('7d');

  // Memoize expensive chart-data derivations so they don't recompute on every
  // render (sort / slice / map on full visit dicts).
  const visitsByDayChart = useMemo(
    () => Object.entries(visitsData?.visits_by_day || {}).map(([date, count]) => ({
      date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      visits: count,
    })),
    [visitsData?.visits_by_day]
  );
  const topCountriesChart = useMemo(
    () => Object.entries(visitsData?.country_stats || {})
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([country, count]) => ({ country, count })),
    [visitsData?.country_stats]
  );

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      logger.error('Error fetching stats:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. Admin privileges required.');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load admin statistics');
      }
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics`, {
        withCredentials: true
      });
      setAnalyticsData(response.data);
    } catch (error) {
      logger.error('Error fetching analytics:', error);
    }
  };

  const fetchRevenue = async () => {
    try {
      const response = await axios.get(`${API}/admin/revenue`, {
        withCredentials: true
      });
      setRevenueData(response.data);
    } catch (error) {
      logger.error('Error fetching revenue:', error);
    }
  };

  const fetchVisits = async () => {
    try {
      const response = await axios.get(`${API}/admin/visits?days=30`, {
        withCredentials: true
      });
      setVisitsData(response.data);
    } catch (error) {
      logger.error('Error fetching visits:', error);
    }
  };

  const fetchDemoAnalytics = async (window = demoAnalyticsWindow) => {
    try {
      const response = await axios.get(`${API}/admin/demo-analytics?window=${window}`, {
        withCredentials: true,
      });
      setDemoAnalytics(response.data);
    } catch (error) {
      logger.error('Error fetching demo analytics:', error);
    }
  };

  const fetchUsers = async (page = 1) => {
    try {
      const skip = (page - 1) * 10;
      const response = await axios.get(`${API}/admin/users?skip=${skip}&limit=10`, {
        withCredentials: true
      });
      setUsers(response.data.users);
      setUsersTotal(response.data.total);
      setTotalPages(response.data.pages);
      setCurrentPage(page);
    } catch (error) {
      logger.error('Error fetching users:', error);
      toast.error('Failed to load users');
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await Promise.all([
      fetchStats(), 
      fetchUsers(currentPage),
      fetchAnalytics(),
      fetchRevenue(),
      fetchVisits(),
      fetchDemoAnalytics(demoAnalyticsWindow)
    ]);
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  // Open edit modal with user data
  const openEditModal = (userToEdit) => {
    const planLimits = { free: 5, student: 50, research: 200, lab: -1 };
    setEditingUser(userToEdit);
    setEditForm({
      name: userToEdit.name || '',
      email: userToEdit.email || '',
      email_verified: userToEdit.email_verified || false,
      is_admin: userToEdit.is_admin || false,
      plan: userToEdit.subscription?.plan || 'free',
      analyses_limit: userToEdit.subscription?.analyses_limit ?? planLimits[userToEdit.subscription?.plan] ?? 5,
      analyses_used: userToEdit.subscription?.analyses_used || 0
    });
    setEditModalOpen(true);
  };

  // Save user changes
  const saveUserChanges = async () => {
    if (!editingUser) return;
    
    try {
      // Update user details
      await axios.put(`${API}/admin/update-user/${editingUser.id}`, {
        name: editForm.name,
        email_verified: editForm.email_verified,
        is_admin: editForm.is_admin
      }, { withCredentials: true });

      // Update subscription
      await axios.post(`${API}/admin/update-subscription-full/${editingUser.id}`, {
        plan: editForm.plan,
        analyses_limit: parseInt(editForm.analyses_limit),
        analyses_used: parseInt(editForm.analyses_used)
      }, { withCredentials: true });

      toast.success('User updated successfully');
      setEditModalOpen(false);
      setEditingUser(null);
      fetchUsers(currentPage);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  // Reset usage counter
  const resetUsage = () => {
    setEditForm(prev => ({ ...prev, analyses_used: 0 }));
  };

  // Set unlimited analyses
  const setUnlimited = () => {
    setEditForm(prev => ({ ...prev, analyses_limit: -1 }));
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchStats(), 
        fetchUsers(1),
        fetchAnalytics(),
        fetchRevenue(),
        fetchVisits(),
        fetchDemoAnalytics(demoAnalyticsWindow)
      ]);
      setLoading(false);
    };
    
    if (user) {
      loadData();
    }
  }, [user]);

  // Re-fetch demo analytics whenever the window selector changes.
  useEffect(() => {
    if (user) {
      fetchDemoAnalytics(demoAnalyticsWindow);
    }
  }, [demoAnalyticsWindow]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  const getPlanColor = (plan) => {
    switch (plan) {
      case 'starter': return 'bg-blue-100 text-blue-700';
      case 'professional': return 'bg-purple-100 text-purple-700';
      case 'unlimited': return 'bg-amber-100 text-amber-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
                data-testid="admin-back-button"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg">
                  <Crown className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-slate-900">Admin Dashboard</h1>
                  <p className="text-sm text-slate-500">Platform Overview</p>
                </div>
              </div>
            </div>
            <Button
              onClick={refreshData}
              disabled={refreshing}
              variant="outline"
              className="gap-2"
              data-testid="refresh-data-button"
            >
              <RefreshCcw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-white/80 backdrop-blur-sm" data-testid="total-users-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Total Users</CardTitle>
              <Users className="w-5 h-5 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900">{stats?.total_users || 0}</div>
              <p className="text-xs text-slate-500 mt-1">
                +{stats?.recent_signups_30d || 0} in last 30 days
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white/80 backdrop-blur-sm" data-testid="total-revenue-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Total Revenue</CardTitle>
              <DollarSign className="w-5 h-5 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900">
                ${stats?.total_revenue?.toFixed(2) || '0.00'}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                MRR: ${stats?.mrr || 0}/month
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white/80 backdrop-blur-sm" data-testid="active-subscriptions-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Active Subscriptions</CardTitle>
              <TrendingUp className="w-5 h-5 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900">{stats?.active_subscriptions || 0}</div>
              <p className="text-xs text-slate-500 mt-1">
                Paid subscribers
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white/80 backdrop-blur-sm" data-testid="total-analyses-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Total Analyses</CardTitle>
              <Activity className="w-5 h-5 text-cyan-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900">{stats?.total_analyses || 0}</div>
              <p className="text-xs text-slate-500 mt-1">
                Reports generated
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Plans Breakdown */}
        <Card className="bg-white/80 backdrop-blur-sm mb-8" data-testid="plans-breakdown-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-500" />
              Subscription Plans Breakdown
            </CardTitle>
            <CardDescription>Distribution of users across different plans</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 rounded-lg bg-slate-50 text-center">
                <div className="text-2xl font-bold text-slate-700">{stats?.plans_breakdown?.free || 0}</div>
                <div className="text-sm text-slate-500">Free</div>
              </div>
              <div className="p-4 rounded-lg bg-blue-50 text-center">
                <div className="text-2xl font-bold text-blue-700">{stats?.plans_breakdown?.starter || 0}</div>
                <div className="text-sm text-blue-600">Starter ($20)</div>
              </div>
              <div className="p-4 rounded-lg bg-purple-50 text-center">
                <div className="text-2xl font-bold text-purple-700">{stats?.plans_breakdown?.professional || 0}</div>
                <div className="text-sm text-purple-600">Professional ($70)</div>
              </div>
              <div className="p-4 rounded-lg bg-amber-50 text-center">
                <div className="text-2xl font-bold text-amber-700">{stats?.plans_breakdown?.unlimited || 0}</div>
                <div className="text-sm text-amber-600">Unlimited ($100)</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Analytics Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* User Growth Chart */}
          <Card className="bg-white/80 backdrop-blur-sm" data-testid="user-growth-chart">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-500" />
                User Growth (Monthly)
              </CardTitle>
              <CardDescription>Monthly new user registrations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                {analyticsData?.monthly_signups ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={Object.entries(analyticsData.monthly_signups).map(([month, count]) => ({
                      month: month,
                      signups: count
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="month" 
                        tick={{ fontSize: 11 }}
                        tickLine={false}
                      />
                      <YAxis 
                        tick={{ fontSize: 10 }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px'
                        }}
                      />
                      <Bar 
                        dataKey="signups" 
                        fill="#3b82f6" 
                        radius={[4, 4, 0, 0]}
                        name="New Users"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400">
                    No data available
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Analyses Chart */}
          <Card className="bg-white/80 backdrop-blur-sm" data-testid="analyses-chart">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-green-500" />
                Analyses (Monthly)
              </CardTitle>
              <CardDescription>Monthly analysis reports generated</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                {analyticsData?.monthly_analyses ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={Object.entries(analyticsData.monthly_analyses).map(([month, count]) => ({
                      month: month,
                      analyses: count
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="month" 
                        tick={{ fontSize: 11 }}
                        tickLine={false}
                      />
                      <YAxis 
                        tick={{ fontSize: 10 }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px'
                        }}
                      />
                      <Bar 
                        dataKey="analyses" 
                        fill="#22c55e" 
                        radius={[4, 4, 0, 0]}
                        name="Analyses"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400">
                    No data available
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Visits Chart */}
          <Card className="bg-white/80 backdrop-blur-sm" data-testid="visits-chart">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LineChartIcon className="w-5 h-5 text-purple-500" />
                Site Visits (Last 30 Days)
              </CardTitle>
              <CardDescription>Daily visitor traffic</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                {visitsData?.visits_by_day ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={visitsByDayChart}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 10 }}
                        tickLine={false}
                      />
                      <YAxis 
                        tick={{ fontSize: 10 }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px'
                        }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="visits" 
                        stroke="#a855f7" 
                        strokeWidth={2}
                        dot={{ fill: '#a855f7', strokeWidth: 0, r: 3 }}
                        name="Visits"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400">
                    No data available
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Country Breakdown */}
          <Card className="bg-white/80 backdrop-blur-sm" data-testid="country-chart">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-cyan-500" />
                Top Countries
              </CardTitle>
              <CardDescription>Visitor distribution by country</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                {visitsData?.country_stats && Object.keys(visitsData.country_stats).length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={topCountriesChart}
                      layout="vertical"
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 10 }} />
                      <YAxis 
                        dataKey="country" 
                        type="category" 
                        tick={{ fontSize: 10 }}
                        width={100}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px'
                        }}
                      />
                      <Bar 
                        dataKey="count" 
                        fill="#06b6d4" 
                        radius={[0, 4, 4, 0]}
                        name="Visitors"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400">
                    No data available
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Demo Analytics — successful demo runs from the /demo tool */}
        <Card className="bg-white/80 backdrop-blur-sm mb-8" data-testid="demo-analytics-card">
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-fuchsia-500" />
                  Demo Analytics
                </CardTitle>
                <CardDescription>
                  Which analyses visitors run in the public demo. Privacy-safe — hashed IP + country only, auto-expires after 30 days.
                </CardDescription>
              </div>
              <div className="flex items-center gap-2" data-testid="demo-analytics-window-selector">
                {['24h', '7d', '30d', 'all'].map((w) => (
                  <button
                    key={w}
                    type="button"
                    onClick={() => setDemoAnalyticsWindow(w)}
                    data-testid={`demo-analytics-window-${w}`}
                    className={
                      demoAnalyticsWindow === w
                        ? 'px-3 py-1 rounded-md text-xs font-medium bg-fuchsia-600 text-white shadow-sm'
                        : 'px-3 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }
                  >
                    {w === 'all' ? 'All-time' : w}
                  </button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {!demoAnalytics ? (
              <div className="text-sm text-slate-400 py-6 text-center">Loading demo analytics…</div>
            ) : demoAnalytics.total === 0 ? (
              <div className="text-sm text-slate-400 py-6 text-center" data-testid="demo-analytics-empty">
                No demo analyses in the selected window.
              </div>
            ) : (
              <>
                {/* Headline number */}
                <div className="flex items-baseline gap-3 mb-4">
                  <div
                    className="text-3xl font-bold text-slate-900"
                    data-testid="demo-analytics-total"
                  >
                    {demoAnalytics.total.toLocaleString()}
                  </div>
                  <div className="text-sm text-slate-500">
                    successful analyses{' '}
                    <span className="text-slate-400">
                      ({demoAnalyticsWindow === 'all' ? 'all-time' : `last ${demoAnalyticsWindow}`})
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Ranking bar chart */}
                  <div className="h-72" data-testid="demo-analytics-ranking-chart">
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                      Most-run analysis types
                    </div>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={demoAnalytics.by_type} layout="vertical" margin={{ left: 10, right: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                        <YAxis
                          dataKey="label"
                          type="category"
                          tick={{ fontSize: 11 }}
                          width={70}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                          }}
                        />
                        <Bar dataKey="count" fill="#c026d3" radius={[0, 4, 4, 0]} name="Runs" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Recent events feed */}
                  <div data-testid="demo-analytics-recent">
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                      Most recent runs
                    </div>
                    <div className="max-h-72 overflow-y-auto rounded-md border border-slate-100">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            <th className="text-left py-2 px-3 text-xs font-medium text-slate-500">Type</th>
                            <th className="text-left py-2 px-3 text-xs font-medium text-slate-500">Country</th>
                            <th className="text-left py-2 px-3 text-xs font-medium text-slate-500">When</th>
                          </tr>
                        </thead>
                        <tbody>
                          {demoAnalytics.recent.length === 0 ? (
                            <tr>
                              <td colSpan={3} className="py-4 px-3 text-slate-400 text-center">
                                No recent events
                              </td>
                            </tr>
                          ) : (
                            demoAnalytics.recent.map((ev) => (
                              <tr
                                key={`${ev.ts}-${ev.analysis_type}-${ev.country}`}
                                className="border-b border-slate-50 last:border-b-0"
                                data-testid="demo-analytics-recent-row"
                              >
                                <td className="py-2 px-3 font-medium text-slate-800">{ev.label}</td>
                                <td className="py-2 px-3 text-slate-600 font-mono text-xs">{ev.country}</td>
                                <td className="py-2 px-3 text-slate-500 text-xs">
                                  {new Date(ev.ts).toLocaleString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Top countries breakdown (compact) */}
                {demoAnalytics.by_country?.length > 0 && (
                  <div className="mt-6" data-testid="demo-analytics-countries">
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                      Top countries
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {demoAnalytics.by_country.map((c) => (
                        <span
                          key={c.country}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-100 text-xs"
                        >
                          <span className="font-mono text-slate-700">{c.country}</span>
                          <span className="text-slate-500">{c.count}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card className="bg-white/80 backdrop-blur-sm" data-testid="users-table-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              All Users ({usersTotal})
            </CardTitle>
            <CardDescription>Manage and view all registered users</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="users-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">User</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Plan</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Usage</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Language</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Joined</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Role</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="py-3 px-4">
                        <div>
                          <div className="font-medium text-slate-900">{u.name}</div>
                          <div className="text-sm text-slate-500">{u.email}</div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPlanColor(u.subscription?.plan)}`}>
                          {u.subscription?.plan || 'free'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-slate-600">
                          {u.subscription?.analyses_used || 0} / {u.subscription?.analyses_limit === -1 ? '∞' : (u.subscription?.analyses_limit || 2)}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-slate-600 uppercase">{u.language || 'en'}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-slate-600">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {u.is_admin ? (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 flex items-center gap-1 w-fit">
                            <Crown className="w-3 h-3" /> Admin
                          </span>
                        ) : (
                          <span className="text-sm text-slate-500">User</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-blue-600 border-blue-300 hover:bg-blue-50"
                          onClick={() => openEditModal(u)}
                          data-testid={`edit-user-${u.id}`}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
                <p className="text-sm text-slate-600">
                  Page {currentPage} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchUsers(currentPage - 1)}
                    disabled={currentPage === 1}
                    data-testid="prev-page-button"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchUsers(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    data-testid="next-page-button"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Edit User Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              <Edit className="w-5 h-5 text-blue-500" />
              Edit User
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Modify user details and subscription settings
            </DialogDescription>
          </DialogHeader>

          {editingUser && (
            <div className="space-y-4 py-4">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="edit-name" className="text-slate-700">Name</Label>
                <Input
                  id="edit-name"
                  value={editForm.name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                  className="border-slate-300"
                />
              </div>

              {/* Email (read-only) */}
              <div className="space-y-2">
                <Label className="text-slate-700">Email</Label>
                <Input
                  value={editForm.email}
                  disabled
                  className="bg-slate-100 border-slate-300 text-slate-500"
                />
              </div>

              {/* Switches Row */}
              <div className="flex gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    id="edit-verified"
                    checked={editForm.email_verified}
                    onCheckedChange={(checked) => setEditForm(prev => ({ ...prev, email_verified: checked }))}
                  />
                  <Label htmlFor="edit-verified" className="text-slate-700">Verified</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="edit-admin"
                    checked={editForm.is_admin}
                    onCheckedChange={(checked) => setEditForm(prev => ({ ...prev, is_admin: checked }))}
                  />
                  <Label htmlFor="edit-admin" className="text-slate-700 flex items-center gap-1">
                    <Crown className="w-4 h-4 text-amber-500" /> Admin
                  </Label>
                </div>
              </div>

              {/* Subscription Plan */}
              <div className="space-y-2">
                <Label className="text-slate-700">Subscription Plan</Label>
                <Select
                  value={editForm.plan}
                  onValueChange={(plan) => {
                    const planLimits = { free: 5, student: 50, research: 200, lab: -1 };
                    setEditForm(prev => ({ 
                      ...prev, 
                      plan, 
                      analyses_limit: planLimits[plan]
                    }));
                  }}
                >
                  <SelectTrigger className="border-slate-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="free">Free (5 analyses/mo)</SelectItem>
                    <SelectItem value="student">Student (50 analyses/mo)</SelectItem>
                    <SelectItem value="research">Research (200 analyses/mo)</SelectItem>
                    <SelectItem value="lab">Lab (Unlimited)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Analyses Limit & Used */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-limit" className="text-slate-700">Analyses Limit</Label>
                  <div className="flex gap-2">
                    <Input
                      id="edit-limit"
                      type="number"
                      value={editForm.analyses_limit}
                      onChange={(e) => setEditForm(prev => ({ ...prev, analyses_limit: e.target.value }))}
                      className="border-slate-300"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={setUnlimited}
                      className="border-purple-400 text-purple-600 hover:bg-purple-50 text-xs px-2"
                    >
                      ∞
                    </Button>
                  </div>
                  <p className="text-xs text-slate-400">-1 = unlimited</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-used" className="text-slate-700">Analyses Used</Label>
                  <div className="flex gap-2">
                    <Input
                      id="edit-used"
                      type="number"
                      value={editForm.analyses_used}
                      onChange={(e) => setEditForm(prev => ({ ...prev, analyses_used: e.target.value }))}
                      className="border-slate-300"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={resetUsage}
                      className="border-blue-400 text-blue-600 hover:bg-blue-50 px-2"
                    >
                      <RefreshCcw className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setEditModalOpen(false)}
              className="border-slate-300 text-slate-700 hover:bg-slate-100"
            >
              Cancel
            </Button>
            <Button
              onClick={saveUserChanges}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboardPage;
